/**
 * useAdminPullRefresh.js — 관리자 레이아웃의 모바일 pull-to-refresh (2026-09-28 AdminLayout.jsx 에서 분리 — 300줄 룰)
 *
 * 동작 변경 없이 옮겼다. location.pathname 만 인자 pathname 으로 받는다.
 * 반환: mainRef(main 요소 ref) · 인디케이터 상태 · main 터치 핸들러 3종.
 */
import { useEffect, useRef, useState } from 'react';
import {
  ADMIN_PULL_TRIGGER_PX,
  ADMIN_PULL_MAX_PX,
  ADMIN_PULL_DAMPING,
} from './menuConfig';

export function useAdminPullRefresh({ isMobile, mobileOpen, pathname }) {
  const [pullDistance,      setPullDistance]      = useState(0);
  const [pullRefreshing,    setPullRefreshing]    = useState(false);

  // pull-to-refresh 상태를 ref로 관리 — 터치 이벤트에서 리렌더 없이 즉시 접근
  const pullStartYRef   = useRef(0);
  const pullActiveRef   = useRef(false);
  const pullDistanceRef = useRef(0);
  // overflow:hidden 레이아웃에서 스크롤 컨테이너는 main 요소이므로 ref 사용
  const mainRef         = useRef(null);

  // ── 라우트·모드 전환 시 pull 상태 초기화 ─────────────────────────────────
  useEffect(() => {
    pullActiveRef.current = false;
    pullDistanceRef.current = 0;
    setPullDistance(0);
  }, [pathname, isMobile, mobileOpen]);

  /**
   * 현재 스크롤 위치 반환
   * overflow:hidden 레이아웃에서는 window가 아닌 main 컨테이너 scrollTop 우선 사용
   */
  function getWindowScrollTop() {
    return mainRef.current?.scrollTop ?? window.scrollY ?? window.pageYOffset ?? 0;
  }

  /**
   * pull-to-refresh 시작 가능 여부 판단
   * 조건: 모바일, 메뉴 닫힘, 새로고침 중 아님, 미팅지도 화면 아님, 스크롤 최상단
   */
  function canStartAdminPullRefresh() {
    if (!isMobile || mobileOpen || pullRefreshing) return false;
    // 미팅지도 화면은 내부에 자체 pull-to-refresh가 있으므로 중복 방지
    if (pathname.startsWith('/admin/meeting-map')) return false;
    return getWindowScrollTop() <= 2;
  }

  /** pull 임계값 도달 시 페이지 새로고침 */
  function runAdminPullRefresh() {
    if (pullRefreshing) return;
    setPullRefreshing(true);
    window.location.reload();
  }

  // ── 터치 이벤트 핸들러 (pull-to-refresh) ──────────────────────────────────

  /**
   * 터치 시작 지점이 스크롤 가능한 자식 요소 안에 있는지 확인
   * overflowY가 auto/scroll이고 실제 스크롤 콘텐츠가 있으면 true
   */
  function hasScrollableAncestor(el) {
    while (el && el !== document.documentElement) {
      const oy = window.getComputedStyle(el).overflowY;
      if ((oy === 'auto' || oy === 'scroll') && el.scrollHeight > el.clientHeight) return true;
      el = el.parentElement;
    }
    return false;
  }

  /**
   * 터치 시작 지점이 data-nopull="true" 속성의 요소 안에 있는지 확인
   * Leaflet 지도처럼 자체 터치 이벤트를 처리하는 영역에서 pull-to-refresh 차단
   */
  function hasNoPullAncestor(el) {
    while (el && el !== document.documentElement) {
      if (el.dataset?.nopull === 'true') return true;
      el = el.parentElement;
    }
    return false;
  }

  function onMainTouchStart(event) {
    if (!event.touches?.length) return;
    pullStartYRef.current = event.touches[0].clientY;
    // 스크롤 가능 자식 요소나 nopull 영역에서 시작된 터치는 비활성
    pullActiveRef.current = canStartAdminPullRefresh()
      && !hasScrollableAncestor(event.target)
      && !hasNoPullAncestor(event.target);
    if (!pullActiveRef.current) {
      pullDistanceRef.current = 0;
      setPullDistance(0);
    }
  }

  function onMainTouchMove(event) {
    if (!pullActiveRef.current || !event.touches?.length) return;
    const delta = event.touches[0].clientY - pullStartYRef.current;
    if (delta <= 0) {
      if (pullDistanceRef.current !== 0) {
        pullDistanceRef.current = 0;
        setPullDistance(0);
      }
      return;
    }
    // 감쇠 적용 후 최대값 클램프
    const damped = Math.min(ADMIN_PULL_MAX_PX, Math.round(delta * ADMIN_PULL_DAMPING));
    pullDistanceRef.current = damped;
    setPullDistance(damped);
    if (event.cancelable) event.preventDefault();
  }

  function onMainTouchEnd() {
    if (!pullActiveRef.current) return;
    pullActiveRef.current = false;
    const distance = pullDistanceRef.current;
    pullDistanceRef.current = 0;
    setPullDistance(0);
    // 임계값 이상 당겼을 때만 새로고침 실행
    if (distance >= ADMIN_PULL_TRIGGER_PX) runAdminPullRefresh();
  }

  // pull 임계값 초과 여부 (인디케이터 색상 변경용)
  const isPullReady = pullDistance >= ADMIN_PULL_TRIGGER_PX;

  return {
    mainRef, pullDistance, pullRefreshing, isPullReady,
    onMainTouchStart, onMainTouchMove, onMainTouchEnd,
  };
}
